import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, useExport, useToast } from '@kss/ui';
import {
  ArrowLeft,
  Download,
  MessageCircle,
  Printer,
  RefreshCw,
} from 'lucide-react';
import {
  companyService,
  customerService,
  entityTranslationService,
  invoiceItemService,
  invoiceService,
  printFormatService,
  translationIndex,
  type Company,
  type Customer,
  type Invoice,
  type InvoiceItem,
  type PrintFormat,
} from '@kss/api';
import { toAbsoluteImageUrl } from '../utils/imageUrl';
// InvoiceDocument lives in the panel package alongside the admin
// preview so both apps render one visual source of truth. Bug ref:
// SSO ISSUE 4 — "customer-facing invoices section displays invoices
// incorrectly". Cross-package import is already the pattern used by
// `panelRoutes.tsx` for every panel page mounted under /panel/*.
import InvoiceDocument from '../../../panel/src/pages/invoice/InvoiceDocument';

/**
 * Customer-facing invoice detail — mirrors the panel's
 * `/panel/invoices/:id/preview` page so the customer sees the same
 * print-ready TAX INVOICE the admin sees. Toolbar carries Print,
 * Download PDF, WhatsApp share.
 *
 * The `useExport` hook (html2canvas + jsPDF, loaded lazily) is the
 * same one the panel uses, so the two "Download PDF" buttons produce
 * byte-for-byte identical files. That equivalence only holds because
 * this page resolves the SAME print format the panel does — see the
 * `printFormatService.effective` call in `load()`. Drop it and the
 * customer copy silently reverts to `InvoiceDocument`'s built-in
 * defaults while the admin copy keeps the tenant's template.
 */
export default function AccountInvoiceDetail() {
  const { id } = useParams<{ id?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { exportToPdf } = useExport();
  const docRef = useRef<HTMLDivElement>(null);
  /* Guard so the auto-download effect fires exactly once per invoice
     load — if the effect re-ran on every state change we'd trigger
     the PDF export repeatedly. Bug ref: SSO ROUND-2 ISSUE 4. */
  const autoDownloadFiredRef = useRef(false);

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [seller, setSeller] = useState<Company | null>(null);
  const [buyer, setBuyer] = useState<Customer | null>(null);
  /* Tenant's invoice print template. Without this the customer copy
     rendered on `InvoiceDocument`'s built-in defaults while the panel
     copy rendered on the configured template — so the same invoice
     printed with different colours, title, terms and section toggles
     depending on who printed it. */
  const [format, setFormat] = useState<PrintFormat | null>(null);
  const [productTranslations, setProductTranslations] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'print' | 'pdf' | 'wa' | null>(null);

  const load = async () => {
    if (!id) {
      setError('Invoice id is missing.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const inv = await invoiceService.get(id);
      setInvoice(inv);
      const tasks = await Promise.allSettled([
        invoiceItemService.filter({ invoiceId: id }),
        inv.customerId ? customerService.get(inv.customerId) : Promise.resolve(null),
        companyService.all(),
        /* Same resolution the panel preview uses — store-specific template
           falling back to the tenant default. `/PrintFormat/effective` is
           `[Authorize]` with no role filter, so a signed-in customer
           resolves it fine. Never blocks the render: on failure the
           document keeps its built-in defaults. */
        printFormatService.effective({ documentType: 'invoice', storeId: inv.storeId }),
      ]);
      if (tasks[0].status === 'fulfilled' && Array.isArray(tasks[0].value)) {
        setItems(tasks[0].value);
      }
      if (tasks[1].status === 'fulfilled' && tasks[1].value) {
        setBuyer(tasks[1].value as Customer);
      }
      if (tasks[2].status === 'fulfilled' && Array.isArray(tasks[2].value)) {
        const list = tasks[2].value as Company[];
        const first = list.find((c) => c.isActive !== false) ?? list[0];
        if (first) setSeller(first);
      }
      if (tasks[3].status === 'fulfilled' && tasks[3].value) {
        const fmt = tasks[3].value as PrintFormat;
        setFormat(fmt);
        /* Product-name translations for the print language — mirrors
           InvoicePreview. Fire-and-forget so a slow lookup never delays
           the invoice; stored names are correct for an untranslated tenant. */
        const lang = fmt.defaultLanguageCode;
        if (lang) {
          entityTranslationService
            .forType('Product', lang)
            .then((rows) => setProductTranslations(translationIndex(rows)))
            .catch(() => { /* untranslated tenant — stored names are correct */ });
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load invoice.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const grandTotal = Number(invoice?.totalAmount ?? 0);

  const handlePrint = () => {
    setBusy('print');
    try {
      window.print();
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async () => {
    if (!docRef.current) return;
    setBusy('pdf');
    try {
      await exportToPdf(
        docRef.current,
        `Invoice-${invoice?.invoiceNumber ?? id ?? 'KRPL'}`,
      );
      toast.success('Invoice downloaded');
    } catch (e) {
      toast.error('Download failed', e instanceof Error ? e.message : 'Try again.');
    } finally {
      setBusy(null);
    }
  };

  /* Auto-download when the caller passes `?action=download` in the URL.
     This is the entry point used by the "Download invoice" affordance
     on the invoice-list row and on the order-details Invoice card —
     the legacy bare-HTML `window.open()` popup they used to show has
     been removed, so both now converge on this single canonical PDF
     export path (same `useExport().exportToPdf(elementRef, filename)`
     the panel calls). We wait until the invoice is loaded AND the doc
     element is mounted before firing, then strip the query param so
     a re-render (or a Back → Forward) doesn't re-trigger. Bug ref:
     SSO ROUND-2 ISSUE 4. */
  useEffect(() => {
    if (autoDownloadFiredRef.current) return;
    if (searchParams.get('action') !== 'download') return;
    if (loading || !invoice || !docRef.current) return;
    autoDownloadFiredRef.current = true;
    /* Give html2canvas a paint tick so the QR image, signature, and
       fonts settle before the snapshot is taken. */
    const t = window.setTimeout(() => {
      void handleDownload();
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      setSearchParams(next, { replace: true });
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, invoice, searchParams]);

  const handleWhatsApp = () => {
    setBusy('wa');
    try {
      const url = window.location.href;
      const lines = [
        `*Invoice ${invoice?.invoiceNumber ?? ''}*`,
        seller?.name ? `From: ${seller.name}` : null,
        buyer?.name ? `Billed to: ${buyer.name}` : null,
        `Amount due: ₹${grandTotal.toLocaleString('en-IN')}`,
        invoice?.dueDate ? `Due by: ${invoice.dueDate.slice(0, 10)}` : null,
        '',
        `View / pay: ${url}`,
      ]
        .filter(Boolean)
        .join('\n');
      const wa = `https://wa.me/?text=${encodeURIComponent(lines)}`;
      window.open(wa, '_blank', 'noopener,noreferrer');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      {/* Action toolbar — hidden in print output via the @media print
          rule below. */}
      <div className="invoice-actions mb-3 flex flex-wrap items-center justify-between gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/account/invoices')}
        >
          <ArrowLeft className="h-3.5 w-3.5" /> All invoices
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            disabled={!invoice || busy === 'print'}
          >
            <Printer className="h-3.5 w-3.5" /> Print
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={!invoice || busy === 'pdf'}
          >
            <Download className="h-3.5 w-3.5" />
            {busy === 'pdf' ? 'Downloading…' : 'Download PDF'}
          </Button>
          <Button
            size="sm"
            onClick={handleWhatsApp}
            disabled={!invoice}
            className="bg-[#25D366] text-white hover:bg-[#1da653]"
          >
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp share
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {loading ? (
        <Card className="p-6 text-center text-sm text-content-muted">
          Loading invoice…
        </Card>
      ) : !invoice ? (
        <Card className="p-6 text-center text-sm text-content-muted">
          Invoice not found.
        </Card>
      ) : (
        <div ref={docRef}>
          <InvoiceDocument
            invoice={invoice}
            items={items}
            seller={seller}
            buyer={buyer}
            signatureSrc={
              invoice.signatureImageUrl
                ? toAbsoluteImageUrl(invoice.signatureImageUrl)
                : ''
            }
            format={format}
            languageCode={format?.defaultLanguageCode}
            productTranslations={productTranslations}
          />
        </div>
      )}

      {/* Print styles — hide everything except the invoice doc and
          force white background / black text. */}
      <style>{`
        @media print {
          .invoice-actions { display: none !important; }
          body { background: #fff !important; }
          .invoice-doc { border: 0 !important; padding: 24px !important; }
          aside, header, footer, nav { display: none !important; }
        }
      `}</style>
    </div>
  );
}
